import { useEffect, useState, useMemo } from 'react';
import { Box, CircularProgress } from '@mui/material';
import CustomText, { textStyles } from "../../../components/CustomText";
import CustomButton from "../../../components/CustomButton";
import BaseCard from '../../../components/Cards/Card';
import CustomPasswordInput from "../../../components/PasswordInput";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from '@mui/material/useMediaQuery';
import { useFormik } from 'formik';
import { toast, Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { BASE_URL } from '../../../components/BaseUrl';
import { showToast } from '../../../Utlis/messageHandler';
/* -------------------- RULE ICON --------------------- */
const RuleRow = ({ ok, label, showError }) => {
  const textColor = ok
    ? '#16a34a'   // green if rule passed
    : showError
      ? '#d32f2f' // red if failed AND user clicked continue
      : '#64748b';// gray by default

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
      <span
        aria-hidden
        style={{
          display: 'inline-flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: ok ? 'none' : '2px solid #9aa4b2',
          background: ok ? '#22c55e' : 'transparent'
        }}
      >
        {ok ? (
          <span
            style={{
              width: 6,
              height: 10,
              borderRight: '2px solid #fff',
              borderBottom: '2px solid #fff',
              transform: 'rotate(45deg)',
            }}
          />
        ) : (
          <span style={{ position: 'relative', width: 10, height: 10 }}>
            <span
              style={{
                position: 'absolute',
                width: '100%',
                height: 2,
                background: showError ? '#d32f2f' : '#9aa4b2',
                transform: 'rotate(45deg)',
                top: '50%',
                left: 0,
                marginTop: -1,
              }}
            />
            <span
              style={{
                position: 'absolute',
                width: '100%',
                height: 2,
                background: showError ? '#d32f2f' : '#9aa4b2',
                transform: 'rotate(-45deg)',
                top: '50%',
                left: 0,
                marginTop: -1,
              }}
            />
          </span>
        )}
      </span>
      <span style={{ fontSize: 14, color: textColor }}>{label}</span>
    </Box>
  );
};

/* -------------------- PASSWORD CHECKLIST --------------------- */
const PasswordChecklist = ({ password, showError }) => {
   const { t } = useTranslation();
  const rules = useMemo(() => ({
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*()[\]{}\-_+=~`|:;"'<>,.?/\\]/.test(password),
  }), [password]);

  return (
 <Box sx={{ mt: 1, mb: { xs: 2, sm: 1.5 } }}>
  <RuleRow ok={rules.length} label={t('passwordRules.minLength')} showError={showError && !rules.length}/>
  <RuleRow ok={rules.upper} label={t('passwordRules.upperCase')} showError={showError && !rules.upper}/>
  <RuleRow ok={rules.lower} label={t('passwordRules.lowerCase')} showError={showError && !rules.lower}/>
  <RuleRow ok={rules.number} label={t('passwordRules.number')} showError={showError && !rules.number}/>
  <RuleRow ok={rules.special} label={t('passwordRules.special')} showError={showError && !rules.special}/>
</Box>
  );
};

/* -------------------- RESET PASSWORD --------------------- */
const ResetPassword = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { resetToken } = useSelector((state) => state.auth);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (!resetToken) {
showToast(toast.error, {
  message_en: t("alerts.resetTokenInvalid"),
  message_es: t("alerts.resetTokenInvalid")
});      navigate("/forget-password");
    }
  }, [resetToken, navigate]);

  const formik = useFormik({
    initialValues: { password: "", confirmPassword: "" },
    validate: (values) => {
      const errors = {};
      const pwd = values.password || "";

      const hasLen = pwd.length >= 8;
      const hasLower = /[a-z]/.test(pwd);
      const hasUpper = /[A-Z]/.test(pwd);
      const hasNum = /[0-9]/.test(pwd);
      const hasSpecial = /[!@#$%^&*()[\]{}\-_+=~`|:;"'<>,.?/\\]/.test(pwd);

      if (!pwd) {
        errors.password = t("resetPassword.passwordRequired") || "Password is required";
      } else if (!(hasLen && hasLower && hasUpper && hasNum && hasSpecial)) {
        errors.password = t("resetPassword.passwordInvalid") || "Password must meet all requirements.";
      }

      if (!values.confirmPassword) {
        errors.confirmPassword = t("resetPassword.confirmPasswordRequired") || "Confirm password is required";
      } else if (values.confirmPassword !== values.password) {
        errors.confirmPassword = t("resetPassword.passwordsMismatch") || "Passwords do not match";
      }

      return errors;
    },
  
    onSubmit: async (values) => {
  console.log("🚀 Reset password form submitted with values:", values);

  // const errors = formik.validate(values);
  const errors = await formik.validateForm(values);

  console.log("Validation errors:", errors);

  if (Object.keys(errors).length > 0) {
    console.warn("⚠️ Validation failed, showing errors");
    setShowError(true);
    formik.setTouched({ password: true, confirmPassword: true });
    const key = Object.keys(errors)[0];
    toast.error(errors[key]);
    return;
  }

  setIsSubmitting(true);
  try {
    console.log("📡 Sending reset request to:", `${BASE_URL}/account-executive/reset-password`);

    const response = await fetch(`${BASE_URL}/account-executive/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        resetToken,
        newPassword: values.password,
        confirmPassword: values.confirmPassword
      }),
    });

    console.log("🌐 Raw response:", response);

    const data = await response.json();
    console.log("✅ Parsed response data:", data);

    if (!response.ok || data.error) {
      console.error("❌ Reset failed:", data);
    showToast(toast.error, data, t("alerts.resetFailed"));

    }

showToast(toast.success, data, t("alerts.resetSuccess"));
navigate("/");
    console.log("🎉 Password reset successful, navigating to home");
    navigate("/");
  } catch (err) {
    console.error("🔥 Error during reset:", err);
showToast(toast.error, err, t("alerts.resetError"));
  } finally {
    console.log("🛑 Reset request finished");
    setIsSubmitting(false);
  }
},

  });

  return (
    <Box sx={{ backgroundColor: "#EFF4F8",  mt:8 }}>
    
      <Box
        sx={{
          px: 2,
          display: "flex",
          justifyContent: "center",
          minHeight: "100vh",
          alignItems: "center",
        }}
      >
        <BaseCard
          maxWidth={isMobile ? "100%" : 380}
          sx={{
            width: isMobile ? "100%" : "auto",
            boxSizing: "border-box",
            px: isMobile ? 2 : 0,
          }}
        >
          <form onSubmit={formik.handleSubmit} noValidate>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 2 }}>
                <CustomText sx={{ ...textStyles.h4, color: theme.palette.tertiary.main }}>
                  {t("resetPassword.title")}
                </CustomText>
                <CustomText sx={{ ...textStyles.body4, color: theme.palette.text.primary }}>
                  {t("resetPassword.subtitle")}
                </CustomText>
              </Box>

              {/* Password */}
              <label>
                {t("resetPassword.passwordLabel")}
                <span style={{ color: "red", marginLeft: 3 }}> *</span>
              </label>
              <CustomPasswordInput
              placeholder='********'
                name="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
              />
              {formik.touched.password && formik.errors.password && (
                <span style={{ color: "red", fontSize: 12 }}>{formik.errors.password}</span>
              )}

              {/* Checklist */}
  

              {/* Confirm Password */}
              <label style={{ marginTop: 5 }}>
                {t("resetPassword.confirmPasswordLabel")}
                <span style={{ color: "red", marginLeft: 3 }}> *</span>
              </label>
              <CustomPasswordInput
              placeholder='********'
                name="confirmPassword"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
              />
              {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                <span style={{ color: "red", fontSize: 12 }}>{formik.errors.confirmPassword}</span>
              )}

                         <PasswordChecklist 
  password={formik.values.password} 
  showError={showError}
/>

              {/* Submit */}
              <CustomButton
                label={
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {isSubmitting && <CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />}
                    {t("resetPassword.submitButton")}
                  </Box>
                }
                type="submit"
                fullWidth
                sx={{ mt: 2 }}
                disabled={isSubmitting}
              />
            </Box>
          </form>
        </BaseCard>
      </Box>
    </Box>
  );
};

export default ResetPassword;










